import { create } from 'zustand'
import type { Pet, PetCreate, PetUpdate } from '@rondela/shared'
import { api, ApiError } from '../../shared/lib/api'

interface PetsState {
  pets: Pet[]
  currentPet: Pet | null
  isLoading: boolean
  error: string | null

  // Actions
  fetchPets: () => Promise<void>
  fetchPet: (id: string) => Promise<void>
  createPet: (data: PetCreate) => Promise<Pet>
  updatePet: (id: string, data: PetUpdate) => Promise<Pet>
  deletePet: (id: string) => Promise<void>
  uploadPhoto: (id: string, file: File) => Promise<string>
  deletePhoto: (id: string) => Promise<void>
  clearError: () => void
  clearCurrentPet: () => void
}

export const usePetsStore = create<PetsState>((set) => ({
  pets: [],
  currentPet: null,
  isLoading: false,
  error: null,

  fetchPets: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ pets: Pet[] }>('/api/pets')
      set({ pets: response.pets, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar mascotas'
      set({ error: message, isLoading: false })
    }
  },

  fetchPet: async (id: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ pet: Pet }>(`/api/pets/${id}`)
      set({ currentPet: response.pet, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar mascota'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  createPet: async (data: PetCreate) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.post<{ pet: Pet }>('/api/pets', data)
      set((state) => ({
        pets: [...state.pets, response.pet],
        isLoading: false,
      }))
      return response.pet
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al crear mascota'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  updatePet: async (id: string, data: PetUpdate) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.put<{ pet: Pet }>(`/api/pets/${id}`, data)
      set((state) => ({
        pets: state.pets.map((p) => (p.id === id ? response.pet : p)),
        currentPet: state.currentPet?.id === id ? response.pet : state.currentPet,
        isLoading: false,
      }))
      return response.pet
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al actualizar mascota'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  deletePet: async (id: string) => {
    try {
      set({ isLoading: true, error: null })
      await api.delete(`/api/pets/${id}`)
      set((state) => ({
        pets: state.pets.filter((p) => p.id !== id),
        currentPet: state.currentPet?.id === id ? null : state.currentPet,
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al eliminar mascota'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  uploadPhoto: async (id: string, file: File) => {
    try {
      set({ isLoading: true, error: null })
      const result = await api.upload<{ url: string }>(`/api/uploads/pets/${id}/photo`, file, 'photo')
      set((state) => ({
        pets: state.pets.map((p) => (p.id === id ? { ...p, fotoUrl: result.url } : p)),
        currentPet: state.currentPet?.id === id ? { ...state.currentPet, fotoUrl: result.url } : state.currentPet,
        isLoading: false,
      }))
      return result.url
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al subir foto'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  deletePhoto: async (id: string) => {
    try {
      set({ isLoading: true, error: null })
      await api.delete(`/api/uploads/pets/${id}/photo`)
      set((state) => ({
        pets: state.pets.map((p) => (p.id === id ? { ...p, fotoUrl: undefined } : p)),
        currentPet: state.currentPet?.id === id ? { ...state.currentPet, fotoUrl: undefined } : state.currentPet,
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al eliminar foto'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  clearError: () => set({ error: null }),
  clearCurrentPet: () => set({ currentPet: null }),
}))
